import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface BarSegment {
  value: number;
  color: string;
  label?: string;
}

interface StackedBarItem {
  label: string;
  segments: BarSegment[];
  total?: number;
}

interface StackedBarChartProps {
  data: StackedBarItem[];
  title?: string;
  height?: number;
  maxValue?: number;
  showValue?: boolean;
  className?: string;
  valueSuffix?: string;
  showLegend?: boolean;
  showYAxis?: boolean;
  yAxisStep?: number;
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  title,
  height = 300,
  maxValue,
  showValue = true,
  className = '',
  valueSuffix = '',
  showLegend = true,
  showYAxis = false,
  yAxisStep = 10,
}) => {
  // Format data for Recharts
  const transformedData = data.map((item) => {
    const result: any = { name: item.label };

    // Sort segments to ensure consistent order (Gold at bottom, Bronze at top)
    const sortedSegments = [...item.segments].sort((a, b) => {
      const order = { Gold: 3, Silver: 2, Bronze: 1 };
      const aOrder = (a.label && order[a.label as keyof typeof order]) || 0;
      const bOrder = (b.label && order[b.label as keyof typeof order]) || 0;
      return aOrder - bOrder;
    });

    sortedSegments.forEach((segment) => {
      if (segment.label) {
        result[segment.label] = segment.value;
      }
    });

    return result;
  });

  // Extract unique segment labels for the bars
  const uniqueSegmentLabels = Array.from(
    new Set(
      data
        .flatMap((item) => item.segments)
        .filter((segment) => segment.label)
        .map((segment) => segment.label),
    ),
  ) as string[];

  // Sort medals in Gold, Silver, Bronze order
  uniqueSegmentLabels.sort((a, b) => {
    const order = { Gold: 1, Silver: 2, Bronze: 3 };
    const aOrder = order[a as keyof typeof order] || 99;
    const bOrder = order[b as keyof typeof order] || 99;
    return aOrder - bOrder;
  });

  // Get colors for each segment
  const colorMap: Record<string, string> = {};
  data.forEach((item) => {
    item.segments.forEach((segment) => {
      if (segment.label) {
        colorMap[segment.label] = segment.color;
      }
    });
  });

  return (
    <div className={`w-full ${className}`}>
      {title && <h3 className="text-lg font-medium mb-2">{title}</h3>}

      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={transformedData}
          margin={{
            top: 5,
            right: 10,
            left: 10,
            bottom: 5,
          }}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tickMargin={5} />
          {showYAxis && (
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) =>
                Number.isInteger(value) ? value.toString() : ''
              }
            />
          )}
          <Tooltip
            formatter={(value: number, name: string) => [`${value}`, name]}
            contentStyle={{ padding: '8px', borderRadius: '4px' }}
          />
          {showLegend && <Legend wrapperStyle={{ paddingTop: 0 }} />}

          {uniqueSegmentLabels.map((segmentLabel) => (
            <Bar
              key={segmentLabel}
              dataKey={segmentLabel}
              stackId="a"
              fill={colorMap[segmentLabel] || '#8884d8'}
              name={segmentLabel}
              label={
                showValue
                  ? { position: 'center', fill: 'white', fontSize: 11 }
                  : false
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StackedBarChart;
